// utils/promptBuilder.js

/**
 * Builds a system prompt with context from the backend for Azure OpenAI chat completion.
 * Designed to prevent hallucination by providing complete data and strict instructions.
 */
export function buildContextAwarePrompt(userMessage, conversationHistory, selectedContext, processContext) {
  if (!selectedContext || selectedContext.isMock) {
    console.warn("⚠️ Sending message without backend context");
    return conversationHistory;
  }

  let contextSections = [];

  // Add live process context with ALL enriched data
  if (processContext) {
    // Global configuration
    const globalConfig = processContext.globalConfig || {};
    const configSection = globalConfig.total_names ? `
GENERATION SETTINGS:
- Target names per project: ${globalConfig.total_names}
- Default mutations per name: ${globalConfig.default_mutations}
- Test mode: ${globalConfig.test_mode_names} names, ${globalConfig.test_mode_mutations} mutations` : "";

    // Model categories summary
    const categories = processContext.modelCategories || {};
    const categoryList = Object.entries(categories)
      .map(([cat, models]) => `• ${cat}: ${models.length} models (${models.slice(0, 3).join(", ")}${models.length > 3 ? "..." : ""})`)
      .join("\n");

    // Mutation types summary
    const mutationTypes = processContext.mutationTypes || {};
    const mutationTypeList = Object.entries(mutationTypes)
      .filter(([_, mutations]) => mutations.length > 0)
      .map(([type, mutations]) => `• ${type}: ${mutations.join(", ")}`)
      .join("\n");

    // Trademark-based mutation rules
    const trademarkRules = processContext.trademarkMutationRules || {};
    const trademarkRuleList = Object.entries(trademarkRules)
      .map(([grade, rule]) => `• Grade ${grade}: ${rule}`)
      .join("\n");

    // Build complete model list with ALL available details
    const modelList = processContext.models
      .map(m => {
        let entry = `• ${m.name} (${m.function})`;
        if (m.category) entry += ` [${m.category}]`;
        if (m.proportion && m.proportion !== 1.0) entry += ` - Proportion: ${m.proportion}x`;
        if (m.description) entry += `\n  Description: ${m.description}`;
        if (m.example) entry += `\n  Example outputs: ${m.example}`;
        if (m.resources && m.resources.length > 0) entry += `\n  Uses: ${m.resources.join(", ")}`;
        if (m.applicable_mutations && m.applicable_mutations.length > 0) {
          entry += `\n  Applicable mutations: ${m.applicable_mutations.slice(0, 5).join(", ")}${m.applicable_mutations.length > 5 ? "..." : ""}`;
        }
        if (m.source) entry += `\n  File: ${m.source}`;
        return entry;
      })
      .join("\n\n");

    // Build complete mutation list with ALL available details
    const mutationList = processContext.mutations
      .map(m => {
        let entry = `• ${m.name} (${m.function})`;
        if (m.mutation_type) entry += ` [${m.mutation_type}]`;
        if (m.description) entry += `\n  Description: ${m.description}`;
        if (m.example) entry += `\n  Example: ${m.example}`;
        if (m.source) entry += `\n  File: ${m.source}`;
        return entry;
      })
      .join("\n\n");

    // Metrics list
    const metricsList = (processContext.metrics || [])
      .map(m => `• ${m.name} (${m.function}): ${m.description || "No description"}`)
      .join("\n");

    contextSections.push(`=== MONIKA NAME GENERATION SYSTEM ===
TOTAL: ${processContext.totalProcesses} processes
- Models (name generation algorithms): ${processContext.models.length}
- Mutations (name transformation functions): ${processContext.mutations.length}
- Metrics: ${(processContext.metrics || []).length}
${configSection}

--- MODEL CATEGORIES ---
${categoryList || "No categories available"}

--- MUTATION TYPES ---
Mutations are classified by intensity:
${mutationTypeList || "No mutation types available"}

--- TRADEMARK-BASED MUTATION SELECTION ---
Which mutations apply depends on the name's trademark grade:
${trademarkRuleList || "No rules available"}

--- ALL ${processContext.models.length} MODELS ---
${modelList}

--- ALL ${processContext.mutations.length} MUTATIONS ---
${mutationList}

--- METRICS (${(processContext.metrics || []).length} total) ---
${metricsList || "No metrics available"}`);
  }

  // Add repository metadata
  contextSections.push(`=== REPOSITORY INFO ===
Repository: ${selectedContext.metadata?.repository || "backend"}
Generated: ${selectedContext.metadata?.generatedAt || "unknown"}
Git: ${selectedContext.metadata?.gitCommit?.substring(0, 8) || "unknown"} (${selectedContext.metadata?.gitBranch || "main"})
Files: ${selectedContext.metadata?.totalFiles || 0} total (${selectedContext.metadata?.pythonFiles || 0} Python, ${
    selectedContext.metadata?.javascriptFiles || 0
  } JavaScript)`);

  if (selectedContext.pythonModules?.length > 0) {
    const moduleList = selectedContext.pythonModules
      .slice(0, 8)
      .map(mod => `• ${mod.file} - ${mod.functions || 0} functions, ${mod.classes || 0} classes`)
      .join("\n");
    contextSections.push(`=== KEY PYTHON MODULES ===\n${moduleList}`);
  }

  if (selectedContext.apiEndpoints?.length > 0) {
    const endpointList = selectedContext.apiEndpoints
      .slice(0, 12)
      .map(ep => `• ${ep.method} ${ep.path} (${ep.file}:${ep.line})`)
      .join("\n");
    contextSections.push(`=== API ENDPOINTS (${selectedContext.apiEndpoints.length} total) ===\n${endpointList}`);
  }

  const modelCount = processContext?.models?.length || 0;
  const mutationCount = processContext?.mutations?.length || 0;
  const totalNames = processContext?.globalConfig?.total_names || "unknown";

  const systemPrompt = {
    role: "system",
    content: `You are Monika's AI assistant - a brand naming tool that generates creative names for companies and products.

=== CRITICAL INSTRUCTIONS ===
1. ONLY use information provided in this context. Do NOT make up or infer details.
2. If asked about something not in the provided data, say "I don't have that information in my current context."
3. When listing models or mutations, use EXACTLY the names and descriptions provided below.
4. Do NOT embellish descriptions with information not explicitly provided.
5. When asked "how does X work?", only describe what's in the provided data. If no implementation details are provided, say so.

=== SYSTEM OVERVIEW ===
Monika generates brand names through this pipeline:

1. PROJECT SETUP: User creates a project with description, target countries, Nice classes, brand attributes, strategy, max character length.

2. LANGUAGE ANALYSIS: System analyzes the project description for linguistic personality fit.

3. NAME GENERATION (${modelCount} Models): Each model generates raw name candidates using different approaches:
   - API-based (Spotify, Reddit, Datamuse)
   - LLM-based (metaphors, personality, homonyms)
   - Database/corpus (place names, first names, books, Wikipedia)
   - Semantic embeddings (evocative nouns, obscure terms)
   Target: ~${totalNames} names per project, distributed by model proportions.

4. METRICS: Each name is graded A-F on:
   - Trademark availability
   - Uniqueness
   - Pronounceability
   - Overall grade (worst of above)

5. MUTATIONS (${mutationCount} types): Names are transformed based on their trademark grade:
   - HEAVY mutations for F-graded names (aggressive changes needed)
   - LIGHT mutations for B/C/D names (subtle tweaks)
   - No mutations for A-graded names (already good)

6. OUTPUT: All names stored with grades and metadata.

=== TERMINOLOGY ===
- "Models" = Name generation algorithms (exactly ${modelCount})
- "Mutations" = Name transformation functions (exactly ${mutationCount})
- "Processes" = Both collectively (${modelCount + mutationCount} total)
- "Proportion" = How many names a model produces relative to others (2.0 = double, 0.5 = half)

=== PROVIDED DATA ===
${contextSections.join("\n\n")}

=== RESPONSE RULES ===
- When asked to "list all models": List ALL ${modelCount} models by name - do not truncate
- When asked to "list all mutations": List ALL ${mutationCount} mutations by name - do not truncate
- When asked about proportions or output amounts: Reference the proportion values (e.g., "Coined Names has proportion 2.0, meaning it produces double the names")
- When asked about mutation types: Explain HEAVY vs LIGHT vs MANUAL
- When asked what mutations apply to a model: Check the "applicable_mutations" field
- When asked about a specific model/mutation: Use ONLY the description, example, and resources provided
- If something is not in the list, say "I don't see [name] in the current process list"
- Always mention exact counts (e.g., "There are ${modelCount} models available")
- Do NOT speculate about internal implementation unless that info is provided

Current user query: ${userMessage}`,
  };

  return [systemPrompt, ...conversationHistory];
}
